/**
 * KPI Form Component
 *
 * Reusable form for creating and editing KPI definitions.
 * Used by the KPI scorecard page for creating and editing KPIs.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Trash2 } from 'lucide-react';
import { useHasAnyRole, useHasPermission } from '@/hooks/useSession';
import { OS_PERMISSIONS } from '@/features/os/permissions';
import { useCreateKpiDefinition, useUpdateKpiDefinition, useDeleteKpiDefinition } from '../hooks/useKpis';
import { LinkedTodoCreator } from '@/features/os-todos';
import { useOwnerOptions } from '@features/rocks/hooks/useRocks';
import { useTeams } from '@features/rocks/hooks/useTeams';
import type { KpiDefinition, KpiCategory, KpiFrequency, NumberFormat, OrientationRule, IndicatorType } from '../types';
import { KPI_CATEGORIES, KPI_FREQUENCIES, NUMBER_FORMATS, ORIENTATION_RULES, INDICATOR_TYPES } from '../types';

interface FormValues {
  name: string;
  description: string;
  category: KpiCategory;
  frequency: KpiFrequency;
  orientation_rule: OrientationRule;
  goal_unit: string;
  goal_value: string;
  goal_min: string;
  goal_max: string;
  number_format: NumberFormat;
  show_total: boolean;
  show_average: boolean;
  show_goal: boolean;
  owner_id: string;
  indicator_type: IndicatorType;
  team_ids: string[];
  is_active: boolean;
}

interface KpiFormProps {
  open: boolean;
  onClose: () => void;
  kpi?: KpiDefinition | null;
  /** Optional footer override. Receives isPending and onClose. */
  renderFooter?: (isPending: boolean, onClose: () => void) => React.ReactNode;
  /** When true, all inputs are disabled and action buttons are hidden. */
  readOnly?: boolean;
}

export function KpiForm({ open, onClose, kpi, renderFooter, readOnly }: KpiFormProps) {
  const isEditing = !!kpi;

  const createKpi = useCreateKpiDefinition();
  const updateKpi = useUpdateKpiDefinition();
  const deleteKpi = useDeleteKpiDefinition();

  // Server gates DELETE with os.kpis.delete; full_admin/director bypass
  // permission strings (matching web_api_server deco.auth — see ProtectedRoute)
  const isPermissionExempt = useHasAnyRole(['full_admin', 'director']);
  const hasDeletePermission = useHasPermission(OS_PERMISSIONS.kpis.delete);
  const canDelete = isPermissionExempt || hasDeletePermission;
  const { data: owners = [] } = useOwnerOptions();
  const { data: teams = [] } = useTeams();

  // Map legacy categories to Carpedia 5 until migration is run
  function mapCategory(cat: string): KpiCategory {
    const mapping: Record<string, KpiCategory> = {
      financial: 'cost',
      sales: 'cost',
      customer: 'experience',
      operations: 'schedule',
      compliance: 'quality',
      other: 'schedule',
    };
    return mapping[cat] ?? (cat as KpiCategory);
  }

  function getFormValues(def?: KpiDefinition | null): FormValues {
    if (def) {
      return {
        name: def.name,
        description: def.description ?? '',
        category: mapCategory(def.category),
        frequency: def.frequency,
        orientation_rule: def.orientation_rule,
        goal_unit: def.goal_unit ?? '',
        goal_value: def.goal_value != null ? String(Number(def.goal_value)) : '',
        goal_min: def.goal_min?.toString() ?? '',
        goal_max: def.goal_max?.toString() ?? '',
        number_format: def.number_format || 'auto',
        show_total: def.show_total,
        show_average: def.show_average,
        show_goal: def.show_goal,
        owner_id: def.owner_id ?? '',
        indicator_type: def.indicator_type ?? 'lagging',
        team_ids: def.team_ids ?? [],
        is_active: def.is_active,
      };
    }
    return {
      name: '',
      description: '',
      category: 'quality',
      frequency: 'monthly',
      orientation_rule: 'gte_goal',
      goal_unit: '',
      goal_value: '',
      goal_min: '',
      goal_max: '',
      number_format: 'auto',
      show_total: false,
      show_average: true,
      show_goal: true,
      owner_id: '',
      indicator_type: 'lagging',
      team_ids: [],
      is_active: true,
    };
  }

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: getFormValues(kpi),
  });

  const orientationRule = watch('orientation_rule');
  const isRangeOrientation = orientationRule === 'inside_range' || orientationRule === 'outside_range';

  // Reset form when kpi or open state changes
  useEffect(() => {
    if (open) {
      reset(getFormValues(kpi));
    }
  }, [open, kpi?.id, reset]);

  const isAutoSaveRef = useRef(false);

  const onSubmit = async (values: FormValues) => {
    try {
      const selectedOwner = values.owner_id
        ? owners.find((o) => o.id === values.owner_id)
        : null;
      const ownerName = selectedOwner?.name ?? null;

      if (isEditing && kpi) {
        await updateKpi.mutateAsync({
          id: kpi.id,
          data: {
            name: values.name,
            description: values.description || null,
            category: values.category,
            frequency: values.frequency,
            orientation_rule: values.orientation_rule,
            goal_unit: values.goal_unit || null,
            goal_value: isRangeOrientation ? null : values.goal_value ? parseFloat(values.goal_value) : null,
            goal_min: isRangeOrientation && values.goal_min ? parseFloat(values.goal_min) : null,
            goal_max: isRangeOrientation && values.goal_max ? parseFloat(values.goal_max) : null,
            number_format: values.number_format,
            show_total: values.show_total,
            show_average: values.show_average,
            show_goal: values.show_goal,
            owner_id: values.owner_id || null,
            owner_name: ownerName,
            indicator_type: values.indicator_type,
            team_ids: values.team_ids,
            is_active: values.is_active,
          },
        });
      } else {
        await createKpi.mutateAsync({
          name: values.name,
          description: values.description || undefined,
          category: values.category,
          frequency: values.frequency,
          orientation_rule: values.orientation_rule,
          goal_unit: values.goal_unit || undefined,
          goal_value: isRangeOrientation ? undefined : values.goal_value ? parseFloat(values.goal_value) : undefined,
          goal_min: isRangeOrientation && values.goal_min ? parseFloat(values.goal_min) : undefined,
          goal_max: isRangeOrientation && values.goal_max ? parseFloat(values.goal_max) : undefined,
          number_format: values.number_format,
          show_total: values.show_total,
          show_average: values.show_average,
          show_goal: values.show_goal,
          owner_id: values.owner_id || undefined,
          owner_name: ownerName || undefined,
          indicator_type: values.indicator_type,
          team_ids: values.team_ids.length > 0 ? values.team_ids : undefined,
        });
      }
      if (!isAutoSaveRef.current) {
        onClose();
      }
    } catch {
      // Error is handled by the mutation hook
    } finally {
      isAutoSaveRef.current = false;
    }
  };

  const isPending = createKpi.isPending || updateKpi.isPending;

  // Auto-save on field change when editing (debounced 1s)
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDelete = async () => {
    if (!kpi) return;
    // Cancel any pending auto-save so it doesn't fire after deletion
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    try {
      await deleteKpi.mutateAsync(kpi.id);
      onClose();
    } catch {
      // Error is handled by the mutation hook
    }
  };

  const autoSave = useCallback(() => {
    if (!isEditing || readOnly) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      isAutoSaveRef.current = true;
      handleSubmit(onSubmit)();
    }, 1000);
  }, [isEditing, readOnly, handleSubmit, onSubmit]);

  // Watch all fields and trigger auto-save on change (edit mode only)
  const allValues = watch();
  const prevValues = useRef(allValues);
  useEffect(() => {
    if (!isEditing || readOnly) return;
    // Skip the initial render
    if (JSON.stringify(prevValues.current) !== JSON.stringify(allValues)) {
      prevValues.current = allValues;
      autoSave();
    }
  }, [allValues, isEditing, readOnly, autoSave]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Name */}
      <div className="space-y-2">
        <Label htmlFor="name">Name *</Label>
        <Input
          id="name"
          placeholder="e.g., Customer Satisfaction Score"
          disabled={readOnly}
          {...register('name', { required: 'Name is required', maxLength: 255 })}
        />
        {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          placeholder="Describe what this KPI measures..."
          className="resize-none"
          rows={2}
          disabled={readOnly}
          {...register('description', { maxLength: 1000 })}
        />
      </div>

      {/* Category and Frequency Row */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Category</Label>
          <Select
            value={watch('category')}
            onValueChange={(v) => setValue('category', v as KpiCategory)}
            disabled={readOnly}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KPI_CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Frequency</Label>
          <Select
            value={watch('frequency')}
            onValueChange={(v) => setValue('frequency', v as KpiFrequency)}
            disabled={readOnly}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KPI_FREQUENCIES.map((freq) => (
                <SelectItem key={freq.value} value={freq.value}>
                  {freq.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Indicator Type */}
      <div className="space-y-2">
        <Label>Indicator Type *</Label>
        <Select
          value={watch('indicator_type')}
          onValueChange={(v) => setValue('indicator_type', v as IndicatorType)}
          disabled={readOnly}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {INDICATOR_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          {INDICATOR_TYPES.find((t) => t.value === watch('indicator_type'))?.description}
        </p>
      </div>

      {/* Owner */}
      <div className="space-y-2">
        <Label>Owner</Label>
        <Select
          value={watch('owner_id') || '_unassigned'}
          onValueChange={(v) => setValue('owner_id', v === '_unassigned' ? '' : v)}
          disabled={readOnly}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select owner..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_unassigned">Unassigned</SelectItem>
            {owners.map((owner) => (
              <SelectItem key={owner.id} value={owner.id}>
                {owner.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Teams */}
      <div className="space-y-2">
        <Label>Teams</Label>
        <div className="space-y-2 max-h-32 overflow-y-auto rounded-md border p-3">
          {teams.map((team) => (
            <div key={team.id} className="flex items-center gap-2">
              <Checkbox
                id={`team-${team.id}`}
                checked={watch('team_ids').includes(team.id)}
                disabled={readOnly}
                onCheckedChange={(checked) => {
                  const current = watch('team_ids');
                  setValue('team_ids', checked
                    ? [...current, team.id]
                    : current.filter((id: string) => id !== team.id));
                }}
              />
              <Label htmlFor={`team-${team.id}`} className="font-normal text-sm">
                {team.name}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Orientation Rule */}
      <div className="space-y-2">
        <Label>Goal Orientation</Label>
        <Select
          value={watch('orientation_rule')}
          onValueChange={(v) => setValue('orientation_rule', v as OrientationRule)}
          disabled={readOnly}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ORIENTATION_RULES.map((rule) => (
              <SelectItem key={rule.value} value={rule.value}>
                {rule.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          {ORIENTATION_RULES.find((r) => r.value === orientationRule)?.description}
        </p>
      </div>

      {/* Goal Configuration */}
      <div className="space-y-4 rounded-lg border p-4">
        <h4 className="text-sm font-medium">Goal Configuration</h4>

        {isRangeOrientation ? (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="goal_min">Min Value</Label>
              <Input
                id="goal_min"
                type="number"
                step="any"
                placeholder="0"
                disabled={readOnly}
                {...register('goal_min')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="goal_max">Max Value</Label>
              <Input
                id="goal_max"
                type="number"
                step="any"
                placeholder="100"
                disabled={readOnly}
                {...register('goal_max')}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="goal_value">Goal Value</Label>
            <Input
              id="goal_value"
              type="number"
              step="any"
              placeholder="e.g., 90"
              disabled={readOnly}
              {...register('goal_value')}
            />
          </div>
        )}
      </div>

      {/* Display Options */}
      <div className="space-y-4 rounded-lg border p-4">
        <h4 className="text-sm font-medium">Display Options</h4>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="goal_unit">Unit</Label>
            <Input
              id="goal_unit"
              placeholder="e.g., %, $, sensors"
              disabled={readOnly}
              {...register('goal_unit', { maxLength: 20 })}
            />
          </div>
          <div className="space-y-2">
            <Label>Number Format</Label>
            <Select
              value={watch('number_format')}
              onValueChange={(v) => setValue('number_format', v as NumberFormat)}
              disabled={readOnly}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NUMBER_FORMATS.map((fmt) => (
                  <SelectItem key={fmt.value} value={fmt.value}>
                    {fmt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {NUMBER_FORMATS.find((f) => f.value === watch('number_format'))?.example
            ? `Preview: ${NUMBER_FORMATS.find((f) => f.value === watch('number_format'))?.example}`
            : ''}
        </p>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="show_average"
              checked={watch('show_average')}
              disabled={readOnly}
              onCheckedChange={(checked) => setValue('show_average', !!checked)}
            />
            <Label htmlFor="show_average" className="font-normal">
              Show average value
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="show_total"
              checked={watch('show_total')}
              disabled={readOnly}
              onCheckedChange={(checked) => setValue('show_total', !!checked)}
            />
            <Label htmlFor="show_total" className="font-normal">
              Show total/sum value
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="show_goal"
              checked={watch('show_goal')}
              disabled={readOnly}
              onCheckedChange={(checked) => setValue('show_goal', !!checked)}
            />
            <Label htmlFor="show_goal" className="font-normal">
              Show goal on scorecard
            </Label>
          </div>
        </div>
      </div>

      {/* Active Status (only when editing) */}
      {isEditing && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Checkbox
              id="is_active"
              checked={watch('is_active')}
              disabled={readOnly}
              onCheckedChange={(checked) => setValue('is_active', !!checked)}
            />
            <Label htmlFor="is_active" className="font-normal">
              KPI is active
            </Label>
          </div>
          <p className="text-xs text-muted-foreground">
            Inactive KPIs are hidden from the scorecard
          </p>
        </div>
      )}

      {/* Linked To-Do (only when editing a real KPI) */}
      {isEditing && kpi && !readOnly && (
        <div className="grid gap-2">
          <Label>Linked To-Do</Label>
          <LinkedTodoCreator
            sourceType="kpi"
            sourceId={kpi.id}
            teamId={kpi.team_ids?.[0] ?? null}
          />
        </div>
      )}

      {!readOnly && (
        isEditing ? (
          /* Edit mode: auto-saves on field change, show saving indicator */
          isPending ? (
            <p className="text-xs text-muted-foreground text-center pt-2">Saving...</p>
          ) : null
        ) : renderFooter ? (
          renderFooter(isPending, onClose)
        ) : (
          /* Create mode: explicit submit button */
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Creating...' : 'Create KPI'}
            </Button>
          </div>
        )
      )}

      {/* Delete (only when editing). Temporarily hiding a KPI is the "KPI is active" toggle above. */}
      {isEditing && kpi && !readOnly && canDelete && (
        <div className="flex items-center justify-between border-t pt-4">
          <p className="text-xs text-muted-foreground">
            Removes this KPI and its history from the scorecard
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={deleteKpi.isPending}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {deleteKpi.isPending ? 'Deleting...' : 'Delete KPI'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete KPI</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{kpi.name}"? It will disappear from the
                  scorecard along with its recorded values.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </form>
  );
}
